import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Activity,
  AlertTriangle,
  CheckCircle2,
  ClipboardList,
  Database,
  HardDrive,
  KeyRound,
  PackageCheck,
  Search,
  RefreshCw,
  Server,
  ShieldCheck,
  Terminal,
} from 'lucide-react'
import { Badge, Button, Card, CardContent, CardHeader, CardTitle, TimeSeriesChart } from '@agion/ui'
import { cn } from '@/core/analytics_utils'
import { queryInstant, queryRange } from '@/core/observability_api_client'
import type { PromQLResponse } from '@/core/observability_types'
import { infraAssetEnvironments, infraAssetKinds, infraOpsAssets } from './infra_assets_data'
import type { InfraAssetEnvironment, InfraAssetHealthMetric, InfraAssetKind, InfraOpsAsset } from './infra_assets_data'

type ObservationState = 'healthy' | 'degraded' | 'unobserved' | 'error'

interface AssetObservation {
  state: ObservationState
  value?: number
  error?: string
}

interface HealthSeriesPoint {
  time: number
  value: number
}

interface HealthSeries {
  metric: InfraAssetHealthMetric
  points: HealthSeriesPoint[]
  error?: string
}

const kindIcons: Record<InfraAssetKind, React.ComponentType<{ className?: string }>> = {
  'dedicated-vm': Server,
  'kubernetes-service': Activity,
  'node-group': HardDrive,
  registry: PackageCheck,
  secrets: KeyRound,
  'managed-data': Database,
}

function readVectorValue(result: unknown): number | null {
  if (
    typeof result === 'object' &&
    result !== null &&
    'value' in result &&
    Array.isArray((result as { value?: unknown }).value)
  ) {
    const raw = (result as { value: [number, string] }).value[1]
    const parsed = Number.parseFloat(raw)
    return Number.isFinite(parsed) ? parsed : null
  }
  return null
}

function classifyObservation(asset: InfraOpsAsset, value: number | null): AssetObservation {
  if (value === null) return { state: 'unobserved' }
  const healthyWhen = asset.healthyWhen ?? 'positive'
  if (healthyWhen === 'zero') {
    return { state: value === 0 ? 'healthy' : 'degraded', value }
  }
  return { state: value > 0 ? 'healthy' : 'degraded', value }
}

function readRangePoints(result: unknown): HealthSeriesPoint[] {
  if (
    typeof result === 'object' &&
    result !== null &&
    'values' in result &&
    Array.isArray((result as { values?: unknown }).values)
  ) {
    return (result as { values: [number, string][] }).values
      .map(([ts, raw]) => ({ time: ts * 1000, value: Number.parseFloat(raw) }))
      .filter(point => Number.isFinite(point.value))
  }
  return []
}

function assertPromQLSuccess(response: PromQLResponse): void {
  if (response.status === 'success') return
  throw new Error(response.error ?? response.errorType ?? 'PromQL query failed')
}

function formatMetricValue(value: number, unit: string): string {
  if (unit === '%') return `${value.toFixed(1)}%`
  if (unit === 'GiB') return `${value.toFixed(2)} GiB`
  if (unit === 'MiB/s') return `${value.toFixed(2)} MiB/s`
  if (unit === 'cores') return `${value.toFixed(2)} cores`
  if (unit === 'nodes' || unit === 'pods' || unit === 'containers' || unit === 'events') return `${value.toFixed(0)} ${unit}`
  return `${value.toFixed(2)} ${unit}`.trim()
}

function metricColor(category: InfraAssetHealthMetric['category']): string {
  if (category === 'availability') return '#059669'
  if (category === 'cpu') return '#2563eb'
  if (category === 'memory') return '#7c3aed'
  if (category === 'disk') return '#d97706'
  if (category === 'network') return '#0891b2'
  if (category === 'restarts') return '#dc2626'
  return '#e11d48'
}

function toChartSeries(series: HealthSeries) {
  return [{
    label: series.metric.label,
    color: metricColor(series.metric.category),
    values: series.points.map(point => ({ date: new Date(point.time).toISOString(), value: point.value })),
  }]
}

function statusVariant(state: ObservationState): 'success' | 'warning' | 'error' | 'muted' {
  if (state === 'healthy') return 'success'
  if (state === 'degraded') return 'error'
  if (state === 'error') return 'warning'
  return 'muted'
}

function criticalityVariant(criticality: InfraOpsAsset['criticality']): 'error' | 'warning' | 'info' {
  if (criticality === 'critical') return 'error'
  if (criticality === 'high') return 'warning'
  return 'info'
}

function kindLabel(kind: InfraAssetKind): string {
  return kind.replace('-', ' ')
}

function environmentLabel(environment: InfraAssetEnvironment): string {
  return environment.replace('-', ' + ')
}

const FilterButton: React.FC<{
  active: boolean
  children: React.ReactNode
  onClick: () => void
}> = ({ active, children, onClick }) => (
  <Button
    variant="ghost"
    onClick={onClick}
    className={cn(
      'h-7 rounded-full px-3 py-1 text-xs font-medium transition-colors',
      active
        ? 'bg-slate-900 text-white hover:bg-slate-800 dark:bg-slate-100 dark:text-slate-900 dark:hover:bg-slate-200'
        : 'bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700'
    )}
  >
    {children}
  </Button>
)

const AssetCard: React.FC<{
  asset: InfraOpsAsset
  observation: AssetObservation
  selected: boolean
  onSelect: () => void
}> = ({ asset, observation, selected, onSelect }) => {
  const { t } = useTranslation('pages')
  const Icon = kindIcons[asset.kind]

  return (
    <Button
      type="button"
      variant="ghost"
      className="block h-auto w-full p-0 text-left"
      onClick={onSelect}
      aria-pressed={selected}
    >
      <Card
        className={cn(
          'overflow-hidden border transition-colors hover:border-slate-300 dark:hover:border-slate-600',
          selected && 'border-slate-900 ring-2 ring-slate-900/10 dark:border-slate-100 dark:ring-slate-100/10'
        )}
      >
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between gap-3">
            <div className="flex min-w-0 items-start gap-3">
              <div className={cn(
                'rounded-md p-2',
                selected ? 'bg-slate-900 dark:bg-slate-100' : 'bg-slate-100 dark:bg-slate-800'
              )}>
                <Icon
                  className={cn(
                    'h-4 w-4',
                    selected ? 'text-white dark:text-slate-900' : 'text-slate-600 dark:text-slate-300'
                  )}
                  aria-hidden="true"
                />
              </div>
              <div className="min-w-0">
                <CardTitle className="truncate text-base">{asset.name}</CardTitle>
                <p className="mt-1 line-clamp-2 text-sm text-slate-500 dark:text-slate-400">{asset.description}</p>
              </div>
            </div>
            <Badge variant={statusVariant(observation.state)} shape="pill">
              {t(`infraAssets.status.${observation.state}`)}
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap gap-2">
            <Badge variant="outline" shape="pill">{environmentLabel(asset.environment)}</Badge>
            <Badge variant="secondary" shape="pill">{kindLabel(asset.kind)}</Badge>
            <Badge variant={criticalityVariant(asset.criticality)} shape="pill">{asset.criticality}</Badge>
          </div>
          <div className="grid gap-2 text-xs text-slate-600 dark:text-slate-300">
            {asset.endpoint && <p className="truncate font-mono">{asset.endpoint}</p>}
          <p className="truncate">{asset.runtime}</p>
        </div>
      </CardContent>
      </Card>
    </Button>
  )
}

const DetailSection: React.FC<{
  title: string
  icon: React.ComponentType<{ className?: string }>
  children: React.ReactNode
}> = ({ title, icon: Icon, children }) => (
  <section className="rounded-md border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-950">
    <div className="mb-3 flex items-center gap-2">
      <Icon className="h-4 w-4 text-slate-500 dark:text-slate-400" aria-hidden="true" />
      <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">{title}</h3>
    </div>
    {children}
  </section>
)

const DefinitionItem: React.FC<{
  label: string
  value?: string | number
  mono?: boolean
}> = ({ label, value, mono }) => (
  <div>
    <p className="text-xs font-medium uppercase text-slate-400 dark:text-slate-500">{label}</p>
    <p className={cn(
      'mt-1 break-words text-sm text-slate-700 dark:text-slate-300',
      mono && 'font-mono text-xs'
    )}>
      {value ?? '-'}
    </p>
  </div>
)

const BulletList: React.FC<{ items: string[] }> = ({ items }) => (
  <ul className="space-y-2 text-sm text-slate-700 dark:text-slate-300">
    {items.map(item => (
      <li key={item} className="flex gap-2">
        <span className="mt-2 h-1.5 w-1.5 flex-none rounded-full bg-slate-400 dark:bg-slate-500" />
        <span>{item}</span>
      </li>
    ))}
  </ul>
)

const AssetDetailPanel: React.FC<{
  asset: InfraOpsAsset
  observation: AssetObservation
  healthSeries: HealthSeries[]
  healthLoading: boolean
}> = ({ asset, observation, healthSeries, healthLoading }) => {
  const { t } = useTranslation('pages')
  const Icon = kindIcons[asset.kind]
  const latestValues = healthSeries
    .map(series => ({ series, latest: series.points[series.points.length - 1]?.value }))
    .filter((entry): entry is { series: HealthSeries; latest: number } => entry.latest !== undefined)

  return (
    <Card className="overflow-hidden">
      <CardHeader className="border-b border-slate-200 bg-slate-50/70 pb-4 dark:border-slate-800 dark:bg-slate-900/40">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
          <div className="flex min-w-0 items-start gap-3">
            <div className="rounded-md bg-slate-900 p-2 dark:bg-slate-100">
              <Icon className="h-5 w-5 text-white dark:text-slate-900" aria-hidden="true" />
            </div>
            <div className="min-w-0">
              <CardTitle className="text-xl">{asset.name}</CardTitle>
              <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">{asset.description}</p>
              <div className="mt-3 flex flex-wrap gap-2">
                <Badge variant={statusVariant(observation.state)} shape="pill">
                  {t(`infraAssets.status.${observation.state}`)}
                </Badge>
                <Badge variant="outline" shape="pill">{environmentLabel(asset.environment)}</Badge>
                <Badge variant="secondary" shape="pill">{kindLabel(asset.kind)}</Badge>
                <Badge variant={criticalityVariant(asset.criticality)} shape="pill">{asset.criticality}</Badge>
              </div>
            </div>
          </div>
          <div className="rounded-md border border-slate-200 bg-white px-3 py-2 dark:border-slate-800 dark:bg-slate-950">
            <p className="text-xs font-medium uppercase text-slate-400 dark:text-slate-500">{t('infraAssets.observedValue')}</p>
            <p className="mt-1 font-mono text-sm text-slate-900 dark:text-slate-100">
              {observation.value === undefined ? t('infraAssets.noSeries') : observation.value.toLocaleString()}
            </p>
            {observation.error && <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">{observation.error}</p>}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4 py-4">
        <DetailSection title={t('infraAssets.healthSaturation', 'Health and saturation')} icon={Activity}>
          <div className="mb-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            {latestValues.length === 0 ? (
              <div className="rounded-md bg-slate-50 p-3 text-sm text-slate-500 dark:bg-slate-900/60 dark:text-slate-400 sm:col-span-2 xl:col-span-4">
                {t('infraAssets.noHealthStats', 'No health metric series are available yet for this asset. The metric cards below show the intended coverage and the missing collector/probe surface.')}
              </div>
            ) : (
              latestValues.slice(0, 4).map(({ series, latest }) => (
                <div key={series.metric.id} className="rounded-md bg-slate-50 p-3 dark:bg-slate-900/60">
                  <p className="text-xs font-medium uppercase text-slate-400 dark:text-slate-500">{series.metric.label}</p>
                  <p className="mt-1 text-xl font-semibold text-slate-900 dark:text-slate-100">
                    {formatMetricValue(latest, series.metric.unit)}
                  </p>
                  <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">{series.metric.category}</p>
                </div>
              ))
            )}
          </div>

          <div className="grid gap-3 xl:grid-cols-2">
            {healthSeries.map(series => (
              <div key={series.metric.id} className="rounded-md border border-slate-200 bg-white p-3 dark:border-slate-800 dark:bg-slate-950">
                <div className="mb-3 flex items-start justify-between gap-3">
                  <div>
                    <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">{series.metric.label}</p>
                    <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">{series.metric.description}</p>
                  </div>
                  <Badge variant={series.points.length > 0 ? 'success' : series.error ? 'warning' : 'muted'} shape="pill">
                    {series.points.length > 0 ? t('infraAssets.seriesLive', 'live') : series.error ? t('infraAssets.seriesError', 'error') : t('infraAssets.seriesMissing', 'missing')}
                  </Badge>
                </div>
                {healthLoading ? (
                  <div className="h-36 animate-pulse rounded-md bg-slate-100 dark:bg-slate-800" />
                ) : series.points.length === 0 ? (
                  <div className="flex h-36 items-center justify-center rounded-md bg-slate-50 px-4 text-center text-sm text-slate-500 dark:bg-slate-900/60 dark:text-slate-400">
                    {series.error ?? series.metric.emptyState}
                  </div>
                ) : (
                  <TimeSeriesChart
                    data={toChartSeries(series)}
                    height={144}
                    showLegend={false}
                    showDots={false}
                    formatY={(value: number) => formatMetricValue(value, series.metric.unit)}
                    formatX={(date: Date) => date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  />
                )}
              </div>
            ))}
          </div>
        </DetailSection>

        <div className="grid gap-4 lg:grid-cols-3">
          <DetailSection title={t('infraAssets.identity', 'Identity')} icon={Server}>
            <div className="grid gap-3">
              <DefinitionItem label={t('infraAssets.owner')} value={asset.owner} />
              <DefinitionItem label={t('infraAssets.runtime')} value={asset.runtime} />
              <DefinitionItem label={t('infraAssets.endpoint')} value={asset.endpoint} mono />
              <DefinitionItem label={t('infraAssets.upcloud')} value={asset.upcloudHostname} mono />
              <DefinitionItem label={t('infraAssets.upcloudUuid')} value={asset.upcloudUuid} mono />
              <DefinitionItem label={t('infraAssets.created')} value={asset.createdAt} />
            </div>
          </DetailSection>

          <DetailSection title={t('infraAssets.operatorQuestions', 'Operator questions')} icon={Search}>
            <BulletList items={asset.operatorQuestions} />
          </DetailSection>

          <DetailSection title={t('infraAssets.firstResponse', 'First response')} icon={ClipboardList}>
            <BulletList items={asset.firstResponse} />
          </DetailSection>
        </div>

        <div className="grid gap-4 xl:grid-cols-[1.1fr_0.9fr]">
          <DetailSection title={t('infraAssets.probes', 'Probe plan')} icon={Activity}>
            <div className="space-y-2">
              {asset.probes.map(probe => (
                <div key={`${probe.kind}-${probe.target}`} className="rounded-md bg-slate-50 p-3 dark:bg-slate-900/60">
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge variant="info" shape="pill">{probe.kind}</Badge>
                    <p className="text-sm font-medium text-slate-900 dark:text-slate-100">{probe.label}</p>
                  </div>
                  <p className="mt-2 break-all font-mono text-xs text-slate-600 dark:text-slate-300">{probe.target}</p>
                  <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">{probe.expectation}</p>
                </div>
              ))}
            </div>
          </DetailSection>

          <DetailSection title={t('infraAssets.telemetryCoverage', 'Telemetry coverage')} icon={ShieldCheck}>
            <div className="space-y-2">
              {asset.telemetry.map(signal => (
                <div key={`${signal.label}-${signal.source}`} className="rounded-md bg-slate-50 p-3 dark:bg-slate-900/60">
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge
                      variant={signal.state === 'available' ? 'success' : signal.state === 'planned' ? 'warning' : 'muted'}
                      shape="pill"
                    >
                      {signal.state}
                    </Badge>
                    <p className="text-sm font-medium text-slate-900 dark:text-slate-100">{signal.label}</p>
                  </div>
                  <p className="mt-1 text-xs font-medium uppercase text-slate-400 dark:text-slate-500">{signal.source}</p>
                  <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">{signal.description}</p>
                </div>
              ))}
            </div>
          </DetailSection>
        </div>

        <div className="grid gap-4 xl:grid-cols-2">
          <DetailSection title={t('infraAssets.failureModes', 'Likely failure modes')} icon={AlertTriangle}>
            <BulletList items={asset.failureModes} />
          </DetailSection>

          <DetailSection title={t('infraAssets.analysisQueries', 'Analysis queries')} icon={Terminal}>
            <div className="space-y-2">
              {asset.analysisQueries.map(query => (
                <div key={`${query.kind}-${query.label}`} className="rounded-md bg-slate-50 p-3 dark:bg-slate-900/60">
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge variant="secondary" shape="pill">{query.kind}</Badge>
                    <p className="text-sm font-medium text-slate-900 dark:text-slate-100">{query.label}</p>
                  </div>
                  <p className="mt-2 overflow-x-auto whitespace-pre-wrap break-all font-mono text-xs text-slate-700 dark:text-slate-300">{query.query}</p>
                  <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">{query.intent}</p>
                </div>
              ))}
            </div>
          </DetailSection>
        </div>

        <div className="grid gap-4 xl:grid-cols-3">
          <DetailSection title={t('infraAssets.dependencies')} icon={CheckCircle2}>
            <div className="flex flex-wrap gap-1.5">
              {asset.dependencies.map(item => <Badge key={item} variant="muted">{item}</Badge>)}
            </div>
          </DetailSection>
          <DetailSection title={t('infraAssets.monitorSignals')} icon={Activity}>
            <div className="flex flex-wrap gap-1.5">
              {asset.monitorSignals.map(item => <Badge key={item} variant="info">{item}</Badge>)}
            </div>
          </DetailSection>
          <DetailSection title={t('infraAssets.sourceRefs')} icon={Database}>
            <div className="space-y-1.5">
              {asset.sourceRefs.map(ref => (
                <div key={ref.path} className="rounded-md bg-slate-50 px-2 py-1.5 dark:bg-slate-900/60">
                  <p className="text-xs font-medium text-slate-700 dark:text-slate-300">{ref.label}</p>
                  <p className="mt-0.5 break-all font-mono text-[11px] text-slate-500 dark:text-slate-400">{ref.path}</p>
                </div>
              ))}
            </div>
          </DetailSection>
        </div>
      </CardContent>
    </Card>
  )
}

export const InfraAssetsPage: React.FC = () => {
  const { t } = useTranslation('pages')
  const [observations, setObservations] = useState<Record<string, AssetObservation>>({})
  const [healthSeriesByAsset, setHealthSeriesByAsset] = useState<Record<string, HealthSeries[]>>({})
  const [healthLoading, setHealthLoading] = useState(false)
  const [loading, setLoading] = useState(true)
  const [envFilter, setEnvFilter] = useState<InfraAssetEnvironment | 'all'>('all')
  const [kindFilter, setKindFilter] = useState<InfraAssetKind | 'all'>('all')
  const [selectedAssetId, setSelectedAssetId] = useState(infraOpsAssets[0]?.id ?? '')

  const fetchObservations = useCallback(async () => {
    setLoading(true)
    const entries = await Promise.all(
      infraOpsAssets.map(async asset => {
        if (!asset.promql) return [asset.id, { state: 'unobserved' } satisfies AssetObservation] as const
        try {
          const response = await queryInstant(asset.promql)
          assertPromQLSuccess(response)
          const value = readVectorValue(response.data?.result?.[0])
          return [asset.id, classifyObservation(asset, value)] as const
        } catch (err) {
          return [
            asset.id,
            {
              state: 'error',
              error: err instanceof Error ? err.message : 'Query failed',
            } satisfies AssetObservation,
          ] as const
        }
      })
    )
    setObservations(Object.fromEntries(entries))
    setLoading(false)
  }, [])

  const loadHealthSeries = useCallback(async (asset: InfraOpsAsset): Promise<HealthSeries[]> => {
    const now = Math.floor(Date.now() / 1000)
    const start = String(now - 3600)
    const end = String(now)
    const step = '60'

    return Promise.all(
      asset.healthMetrics.map(async metric => {
        try {
          const response = await queryRange(metric.query, start, end, step)
          assertPromQLSuccess(response)
          return {
            metric,
            points: readRangePoints(response.data?.result?.[0]),
          } satisfies HealthSeries
        } catch (err) {
          return {
            metric,
            points: [],
            error: err instanceof Error ? err.message : metric.emptyState,
          } satisfies HealthSeries
        }
      })
    )
  }, [])

  useEffect(() => {
    fetchObservations()
    const interval = window.setInterval(fetchObservations, 60000)
    return () => window.clearInterval(interval)
  }, [fetchObservations])

  const filteredAssets = useMemo(() => (
    infraOpsAssets.filter(asset => {
      const envMatches = envFilter === 'all' || asset.environment === envFilter
      const kindMatches = kindFilter === 'all' || asset.kind === kindFilter
      return envMatches && kindMatches
    })
  ), [envFilter, kindFilter])

  useEffect(() => {
    if (filteredAssets.length === 0) return
    if (!filteredAssets.some(asset => asset.id === selectedAssetId)) {
      setSelectedAssetId(filteredAssets[0]!.id)
    }
  }, [filteredAssets, selectedAssetId])

  const selectedAsset = useMemo(
    () => infraOpsAssets.find(asset => asset.id === selectedAssetId) ?? filteredAssets[0],
    [filteredAssets, selectedAssetId]
  )

  useEffect(() => {
    if (!selectedAsset) return
    const asset = selectedAsset
    let cancelled = false

    async function refreshSelectedHealthSeries(showLoading: boolean) {
      if (showLoading) setHealthLoading(true)
      const entries = await loadHealthSeries(asset)
      if (!cancelled) {
        setHealthSeriesByAsset(prev => ({ ...prev, [asset.id]: entries }))
        if (showLoading) setHealthLoading(false)
      }
    }

    void refreshSelectedHealthSeries(true)
    const interval = window.setInterval(() => {
      void refreshSelectedHealthSeries(false)
    }, 60000)
    return () => {
      cancelled = true
      window.clearInterval(interval)
    }
  }, [loadHealthSeries, selectedAsset])

  const refreshAll = useCallback(async () => {
    const asset = selectedAsset
    if (asset) setHealthLoading(true)
    try {
      const [, healthEntries] = await Promise.all([
        fetchObservations(),
        asset ? loadHealthSeries(asset) : Promise.resolve(null),
      ])
      if (asset && healthEntries) {
        setHealthSeriesByAsset(prev => ({ ...prev, [asset.id]: healthEntries }))
      }
    } finally {
      if (asset) setHealthLoading(false)
    }
  }, [fetchObservations, loadHealthSeries, selectedAsset])

  const summary = useMemo(() => {
    const values = infraOpsAssets.map(asset => observations[asset.id]?.state ?? 'unobserved')
    return {
      total: infraOpsAssets.length,
      healthy: values.filter(state => state === 'healthy').length,
      degraded: values.filter(state => state === 'degraded' || state === 'error').length,
      unobserved: values.filter(state => state === 'unobserved').length,
    }
  }, [observations])

  return (
    <div className="container-standard">
      <div className="page-header">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div className="flex items-center gap-3">
            <ShieldCheck className="h-6 w-6 text-slate-600 dark:text-slate-400" aria-hidden="true" />
            <div>
              <h1 className="page-title">{t('infraAssets.title')}</h1>
              <p className="text-sm text-slate-500 dark:text-slate-400">{t('infraAssets.description')}</p>
            </div>
          </div>
          <Button
            variant="outline"
            onClick={refreshAll}
            className="h-9 px-3"
            aria-label={t('infraAssets.refresh')}
          >
            <RefreshCw className={cn('h-4 w-4', loading && 'animate-spin')} aria-hidden="true" />
            <span>{t('infraAssets.refresh')}</span>
          </Button>
        </div>
      </div>

      <div className="mb-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardContent className="flex items-center gap-3 py-4">
            <div className="rounded-md bg-slate-900 p-2 dark:bg-slate-100">
              <HardDrive className="h-5 w-5 text-white dark:text-slate-900" aria-hidden="true" />
            </div>
            <div>
              <p className="text-sm text-slate-500 dark:text-slate-400">{t('infraAssets.totalAssets')}</p>
              <p className="text-2xl font-semibold text-slate-900 dark:text-slate-100">{summary.total}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="flex items-center gap-3 py-4">
            <div className="rounded-md bg-emerald-600 p-2">
              <ShieldCheck className="h-5 w-5 text-white" aria-hidden="true" />
            </div>
            <div>
              <p className="text-sm text-slate-500 dark:text-slate-400">{t('infraAssets.healthy')}</p>
              <p className="text-2xl font-semibold text-slate-900 dark:text-slate-100">{summary.healthy}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="flex items-center gap-3 py-4">
            <div className="rounded-md bg-rose-600 p-2">
              <AlertTriangle className="h-5 w-5 text-white" aria-hidden="true" />
            </div>
            <div>
              <p className="text-sm text-slate-500 dark:text-slate-400">{t('infraAssets.degraded')}</p>
              <p className="text-2xl font-semibold text-slate-900 dark:text-slate-100">{summary.degraded}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="flex items-center gap-3 py-4">
            <div className="rounded-md bg-sky-600 p-2">
              <Activity className="h-5 w-5 text-white" aria-hidden="true" />
            </div>
            <div>
              <p className="text-sm text-slate-500 dark:text-slate-400">{t('infraAssets.unobserved')}</p>
              <p className="text-2xl font-semibold text-slate-900 dark:text-slate-100">{summary.unobserved}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="mb-6">
        <CardContent className="space-y-4 py-4">
          <div>
            <p className="mb-2 text-xs font-medium uppercase text-slate-400 dark:text-slate-500">{t('infraAssets.environment')}</p>
            <div className="flex flex-wrap gap-2">
              <FilterButton active={envFilter === 'all'} onClick={() => setEnvFilter('all')}>{t('observability.all')}</FilterButton>
              {infraAssetEnvironments.map(environment => (
                <FilterButton key={environment} active={envFilter === environment} onClick={() => setEnvFilter(environment)}>
                  {environmentLabel(environment)}
                </FilterButton>
              ))}
            </div>
          </div>
          <div>
            <p className="mb-2 text-xs font-medium uppercase text-slate-400 dark:text-slate-500">{t('infraAssets.kind')}</p>
            <div className="flex flex-wrap gap-2">
              <FilterButton active={kindFilter === 'all'} onClick={() => setKindFilter('all')}>{t('observability.all')}</FilterButton>
              {infraAssetKinds.map(kind => (
                <FilterButton key={kind} active={kindFilter === kind} onClick={() => setKindFilter(kind)}>
                  {kindLabel(kind)}
                </FilterButton>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-4 xl:grid-cols-[minmax(320px,0.45fr)_minmax(0,1fr)]">
        {filteredAssets.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center text-sm text-slate-500 dark:text-slate-400">
              {t('infraAssets.noAssets')}
            </CardContent>
          </Card>
        ) : (
          <>
            <div className="space-y-3">
              <div>
                <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-400 dark:text-slate-500">
                  {t('infraAssets.assetList', 'Assets')}
                </h2>
              </div>
              {filteredAssets.map(asset => (
                <AssetCard
                  key={asset.id}
                  asset={asset}
                  selected={asset.id === selectedAsset?.id}
                  observation={observations[asset.id] ?? { state: 'unobserved' }}
                  onSelect={() => setSelectedAssetId(asset.id)}
                />
              ))}
            </div>
            {selectedAsset && (
              <AssetDetailPanel
                asset={selectedAsset}
                observation={observations[selectedAsset.id] ?? { state: 'unobserved' }}
                healthSeries={healthSeriesByAsset[selectedAsset.id] ?? selectedAsset.healthMetrics.map(metric => ({ metric, points: [] }))}
                healthLoading={healthLoading}
              />
            )}
          </>
        )}
      </div>
    </div>
  )
}
